import React, { useState, useEffect } from 'react';

const ImageDisplay = ({ imageUrl, gameStatus, isPromptGiver, promptCreationContent, guesses, players, currentPrompt, isPromptRevealed, socketId, gameState, userId }) => {
    const [currentTipIndex, setCurrentTipIndex] = useState(0);
    const [selectedTip, setSelectedTip] = useState(0);

    const getPromptGiverName = () => {
        const promptGiverPlayer = players?.find(p => p.id === gameState?.promptGiver?.id);
        return promptGiverPlayer?.name || 'our artist';
    };

    const getStyleDisplayName = (style) => {
        console.log('Style input:', style);
        console.log('Current image style:', gameState?.currentImage?.style);

        // If the style is 'random' or not set, try to get the actual style from currentImage
        if (!style || style === 'random') {
            // Get the actual style from currentImage
            const actualStyle = gameState?.currentImage?.style;
            console.log('Actual style found:', actualStyle);
            // If we have an actual style, show its description
            if (actualStyle) {
                const styleMap = {
                    'expression': 'expressive, emotional, bold brushstrokes, intense colors',
                    'abstract': 'abstract art style, non-representational, geometric shapes and colors',
                    'surreal': 'surrealist art style, dreamlike, fantastical elements, Salvador Dali inspired',
                    'naive': 'naive art style, childlike, folk art, primitive, Henri Rousseau inspired',
                    'minimal': 'minimalist art style, simple forms, limited palette, clean lines',
                    'anime': 'anime style, manga art, Japanese animation',
                    'ink': 'traditional ink painting, brushwork, flowing lines, Asian art inspired'
                };
                return styleMap[actualStyle] || actualStyle;
            }
            // If no actual style yet, return 'Random'
            return 'Random';
        }

        // For explicitly selected styles, return the capitalized short title
        return style.charAt(0).toUpperCase() + style.slice(1);
    };

    const tips = [
        {
            title: "How It Works",
            content: "Each round, someone dreams up a scene in 5 words, and AI brings it to life. Put on your detective hat and start guessing!",
            icon: "🎨"
        },
        {
            title: "AI Judgment Time",
            content: "After each guess, our AI judge awards you a special badge based on how close you got. Will you impress the algorithm this round?",
            icon: "🤖"
        },

        {
            title: "Pro Tips",
            content: "Look for the little details - maybe there's a sneaky red balloon or a dancing cat! Sometimes the wildest guess turns out to be spot on.",
            icon: "🔍"
        }
    ];

    useEffect(() => {
        if (gameStatus === 'waiting' && !isPromptGiver) {
            const interval = setInterval(() => {
                setCurrentTipIndex((prev) => (prev + 1) % tips.length);
            }, 8000); // Slowed down the rotation
            return () => clearInterval(interval);
        }
    }, [gameStatus, isPromptGiver]);

    const renderWaitingContent = () => {
        const promptGiverName = getPromptGiverName();

        return (
            <div className="waiting-prompt">
                <div className="waiting-content">
                    <div className="status-message">
                        <div className="creative-space">
                            <div className="thought-bubble">
                                <div className="bubble-dots">
                                    <span>•</span>
                                    <span>•</span>
                                    <span>•</span>
                                </div>
                                <div className="artist-canvas">
                                    <div className="artist-palette"></div>
                                    <div className="artist-brush"></div>
                                </div>
                            </div>
                        </div>
                        <h2 className="waiting-text">{promptGiverName} is cooking up something amazing...</h2>
                    </div>

                    <div className="tutorial-section">
                        <div className="tip-navigation">
                            {tips.map((tip, index) => (
                                <button
                                    key={index}
                                    className={`tip-tab ${selectedTip === index ? 'active' : ''}`}
                                    onClick={() => setSelectedTip(index)}
                                >
                                    <span className="tip-tab-icon">{tip.icon}</span>
                                    {tip.title}
                                </button>
                            ))}
                        </div>
                        <div className="tip-content">
                            <p className="tip-description">{tips[selectedTip].content}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderContent = () => {
        // Initial state when just joined
        if (!gameStatus) {
            return (
                <div className="waiting-prompt">
                    <span className="waiting-icon">👋</span>
                    <span className="waiting-text">Welcome to Guess What?!</span>
                    <div className="welcome-tutorial">
                        <p>Get ready to unleash your creativity and guessing skills!</p>
                        <div className="game-steps">
                            <div className="step">1️⃣ Someone creates a prompt</div>
                            <div className="step">2️⃣ AI generates art</div>
                            <div className="step">3️⃣ Everyone tries to guess</div>
                            <div className="step">4️⃣ Points for close matches!</div>
                        </div>
                    </div>
                </div>
            );
        }

        // Image is ready
        if (imageUrl) {
            const userGuess = guesses.find(g => g.playerId === userId);
            return (
                <>
                    <div className="image-reveal-container">
                        <div className="image-frame">
                            <div className="image-container">
                                <img src={imageUrl} alt="AI generated art" />
                                <div className="image-overlay"></div>
                                <div className="sparkle-effect">
                                    <span>✨</span>
                                    <span>✨</span>
                                    <span>✨</span>
                                </div>
                            </div>
                        </div>

                        <div className="prompt-info-container">
                            {(isPromptGiver || isPromptRevealed) && currentPrompt && (
                                <div className={`prompt-display floating ${isPromptRevealed ? 'revealed' : ''}`}>
                                    <span className="prompt-decoration left">✨</span>
                                    <span className="prompt-text">{currentPrompt}</span>
                                    <span className="prompt-decoration right">✨</span>
                                </div>
                            )}
                            {!isPromptGiver && !isPromptRevealed && currentPrompt && (
                                <div className="word-count-display">
                                    <div className="word-count-message">
                                        <span className="player-name">{getPromptGiverName()}</span>'s <span className="art-style">{getStyleDisplayName(gameState?.currentImage?.style || gameState?.settings?.artStyle)}</span> art in
                                        <span className="word-count-number"> {currentPrompt.trim().split(/\s+/).length} </span>
                                        words
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            );
        }

        // Generating image after prompt submission
        if (gameStatus === 'generating') {
            return (
                <div className="loading-animation">
                    <div className="loading-art">
                        <div className="loading-canvas">
                            <div className="loading-brush"></div>
                            <div className="loading-palette"></div>
                            <div className="loading-sparkles">
                                <span>✨</span>
                                <span>✨</span>
                                <span>✨</span>
                            </div>
                        </div>
                    </div>
                    {isPromptGiver && (
                        <div className="prompt-display generating">
                            <span className="prompt-text">{currentPrompt}</span>
                        </div>
                    )}
                    <div className="loading-text">AI is painting you a masterpiece...</div>
                    <div className="loading-progress">
                        <div className="loading-bar">
                            <div className="loading-bar-fill"></div>
                        </div>
                        {/* <div className="loading-message">
                            <p>DALL·E is crafting the perfect image</p>
                            <p className="loading-subtext">This usually takes about 10-15 seconds</p>
                        </div> */}
                    </div>
                </div>
            );
        }

        // Waiting for prompt - show creation UI for prompt giver, waiting message for others
        if (gameStatus === 'waiting') {
            if (isPromptGiver && promptCreationContent) {
                return (
                    <div className="prompt-creation-container">
                        {promptCreationContent}
                    </div>
                );
            }
            return renderWaitingContent();
        }

        // Default state
        return (
            <div className="waiting-prompt">
                <span className="waiting-icon">🖼️</span>
                <span className="waiting-text">Image will appear here</span>
            </div>
        );
    };

    return (
        <div className={`image-display ${gameStatus === 'generating' ? 'loading' : ''}`}>
            {renderContent()}
        </div>
    );
};

export default ImageDisplay;